import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { message as antMessage } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { routes, subRoutes } from '../../utils/constants/routes';

const QuotesContext = createContext({});

export const QuotesContextProvider = ({ children }) => {
  const navigate = useNavigate();
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const [exemptions, setExemptions] = useState([]);
  const [formValues, setFormValues] = useState(null);
  const [initialContractValues, setInitialContractValues] = useState(null);
  const [inseeLoading, setInseeLoading] = useState(false);
  const [displayRestricted, setDisplayRestricted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [formModalValues, setFormModalValues] = useState({});
  const [selectedQuote, setSelectedQuote] = useState();
  const [selectedContract, setSelectedContract] = useState();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [codesNaf, setCodesNaf] = useState([]);
  const [activitiesAsso, setActivitiesAsso] = useState([]);
  const [activitiesHiscox, setActivitiesHiscox] = useState([]);
  const { dispatchAPI } = useAuthContext();
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [siretStatus, setSiretStatus] = useState(undefined);
  const [rnaStatus, setRnaStatus] = useState(false);
  const [visibleMailConfirm, setVisibleMailConfirm] = useState(false);
  const [enums, setEnums] = useState();
  const [isEstimationLoading, setIsEstimationLoading] = useState(false);
  const [postalCode, setPostalCode] = useState();
  const [catNat, setCatNat] = useState();
  const [analyzeModalVisible, setAnalyzeModalVisible] = useState(false);
  const [dataModal, setDataModal] = useState(undefined);
  const [estimationAvoider, setEstimationAvoider] = useState(false);
  const [eligiblePricing, setEligiblePricing] = useState(true);
  const [errorGetCities, setErrorGetCities] = useState(false);

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/contracts/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getCodesNaf = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/activities?status=ENABLED'
      });
      setCodesNaf(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getActivityAsso = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/activitiesassos?status=ENABLED'
      });
      setActivitiesAsso(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getActivityHiscox = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: '/activities/hiscox'
      });
      setActivitiesHiscox(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  useEffect(() => {
    (async () => {
      await getCodesNaf();
      await getActivityAsso();
      await getActivityHiscox();
      await getEnums();
    })();
  }, []);

  const updateQuote = async (id, body) => {
    try {
      const { status, data } = await dispatchAPI('PATCH', {
        url: `/contracts/${id}`,
        body
      });
      if (status === 200) {
        antMessage.success(t('validate_quote.messages.success'));
      }
      setForceRefresh(!forceRefresh);
      setSelectedQuote(null);
      setSelectedContract(null);
      setFormModalValues(null);
      setVisible(false);
      setAnalyzeModalVisible(false);
      navigate(
        `${routes.CONTRACTS}${subRoutes.CONTRACTS.PORTFOLIO_CONTRACTS}/show/${
          data?._id
        }${
          data.paperwork_fee && data.paperwork_fee_payment === 'CB'
            ? ''
            : '?action=print_sign'
        }${
          data.paperwork_fee && data.paperwork_fee_payment === 'CB' ? '?' : '&'
        }${data.payment_type === 'CARD' ? 'action=quittance_payment' : ''}`
      );
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const handleOpenModal = (key, type, contract) => {
    setVisible(true);
    setSelectedContract(contract);
    setSelectedQuote({ key, type });
  };

  const validateTicket = async (numberExemption) => {
    try {
      await dispatchAPI('PATCH', {
        url: `/tickets/${selectedContract?.exemption?._id}`,
        body: {
          status: 'ACCEPTED',
          idContract: selectedContract?._id,
          numberExemption
        }
      });
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const validateQuote = async (tracfin) => {
    if (selectedContract?.exemption) {
      await validateTicket('deleteExemption');
    }
    await updateQuote(selectedQuote.key, {
      ...formModalValues,
      status: 'ACTIVE',
      type: selectedQuote.type,
      tracfin
    });
  };

  const handleCancel = () => {
    setSelectedQuote(null);
    setSelectedContract(null);
    setFormModalValues(null);
    setVisible(false);
  };

  const getEstimation = async (values, type, supplier) => {
    const typeBody = supplier
      ? type.concat(supplier).toUpperCase()
      : type.toUpperCase();

    if (!!estimationAvoider && estimationAvoider === typeBody) {
      return;
    }

    setIsEstimationLoading(true);
    try {
      const { data } = await dispatchAPI('POST', {
        url: '/quotations/estimation',
        body: { ...values, type: typeBody.toUpperCase() }
      });

      let newEstimatedCost = data;

      if (!(data?.total_monthly_customer > 0)) {
        newEstimatedCost = {
          total_monthly_customer: 0,
          remuneration: 0,
          error: data?.error || null
        };
      }

      setEstimatedCost(newEstimatedCost);

      if (data?.cat_nat_risk) {
        setCatNat(!!data.cat_nat_risk);
      }

      setIsEstimationLoading(false);
      return newEstimatedCost;
    } catch (e) {
      if (e.response) message(e.response.status);
      setIsEstimationLoading(false);
    }
  };

  // eslint-disable-next-line consistent-return
  const getEstimationLazy = async (values, type, supplier) => {
    const typeBody = supplier
      ? type.concat(supplier).toUpperCase()
      : type.toUpperCase();
    try {
      const { data } = await dispatchAPI('POST', {
        url: '/quotations/estimation',
        body: { ...values, type: typeBody.toUpperCase() }
      });
      return data.total_monthly_customer;
    } catch (e) {
      if (e.response) return message(e.response.status);
    }
  };

  const setFormFieldValue = (field, value) => {
    if (!formValues) {
      return;
    }

    setFormValues((formValues) => ({
      ...formValues,
      [field]: value
    }));
  };

  return (
    <QuotesContext.Provider
      value={{
        exemptions,
        setExemptions,
        formValues,
        setFormValues,
        setFormFieldValue,
        inseeLoading,
        setInseeLoading,
        displayRestricted,
        setDisplayRestricted,
        codesNaf,
        activitiesAsso,
        activitiesHiscox,
        handleOpenModal,
        visible,
        setVisible,
        setFormModalValues,
        formModalValues,
        validateQuote,
        selectedQuote,
        handleCancel,
        forceRefresh,
        setForceRefresh,
        getEstimation,
        getEstimationLazy,
        estimatedCost,
        setEstimatedCost,
        isEstimationLoading,
        setInitialContractValues,
        initialContractValues,
        siretStatus,
        setSiretStatus,
        rnaStatus,
        setRnaStatus,
        visibleMailConfirm,
        setVisibleMailConfirm,
        enums,
        postalCode,
        setPostalCode,
        catNat,
        analyzeModalVisible,
        setAnalyzeModalVisible,
        dataModal,
        setDataModal,
        setEstimationAvoider,
        estimationAvoider,
        eligiblePricing,
        setEligiblePricing,
        errorGetCities,
        setErrorGetCities
      }}
    >
      {children}
    </QuotesContext.Provider>
  );
};

QuotesContextProvider.propTypes = {
  children: PropTypes.element.isRequired
};

const useQuotesContext = () => useContext(QuotesContext);

export default useQuotesContext;
